import React, { useState, useEffect } from 'react';
import { get, post } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => '₦' + (parseFloat(n)||0).toLocaleString('en-NG', {minimumFractionDigits:2});
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function StewardContributions() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [summary, setSummary] = useState([]);
  const [totalLiability, setTotalLiability] = useState(0);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffDetail, setStaffDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    payment_date:'', payment_method:'', exit_type:'proper', reference:'', notes:''
  });
  const [manualForm, setManualForm] = useState({
    staff_id:'', month:'', year:'', salary_at_time:'', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [myData, setMyData] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) {
      loadSummary();
    } else {
      loadMyData();
    }
  }, []);

  const loadSummary = () => {
    setLoading(true);
    get('/steward-contributions/summary').then(d => {
      setSummary(d.data||[]);
      setTotalLiability(d.total_liability||0);
    }).finally(() => setLoading(false));
  };

  const loadMyData = () => {
    setLoading(true);
    get('/steward-contributions/my').then(d => {
      setMyData(d);
    }).finally(() => setLoading(false));
  };

  const loadStaffDetail = (staff) => {
    setSelectedStaff(staff);
    get('/steward-contributions/staff/'+staff.staff_id).then(d => setStaffDetail(d));
  };

  const processPayout = async () => {
    if (!payoutForm.payment_date || !payoutForm.payment_method) {
      alert('Please fill all required fields'); return;
    }
    setSaving(true);
    try {
      await post('/steward-contributions/payout/'+selectedStaff.staff_id, payoutForm);
      setShowPayoutModal(false);
      loadSummary();
      loadStaffDetail(selectedStaff);
      alert('Payout processed successfully');
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const processManual = async () => {
    setSaving(true);
    try {
      await post('/steward-contributions/manual', manualForm);
      setShowManualModal(false);
      loadSummary();
      if (selectedStaff) loadStaffDetail(selectedStaff);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  // ── STEWARD VIEW ─────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    if (loading) return <div className="page"><div className="spinner"/></div>;

    return (
      <div className="page">
        <div style={{maxWidth:680,margin:'0 auto'}}>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',
            color:'#3B2A21',marginBottom:4}}>My Loyalty Wallet</h2>
          <p style={{color:'#8A8A8A',fontSize:13,marginBottom:28}}>
            Bloans sets aside 10% of your salary every month as a loyalty gift. This is not deducted from your pay. It is yours to collect when you exit properly.
          </p>

          {myData?.no_start_date && (
            <div style={{background:'#F9F8F5',border:'1px solid #E8E0D4',borderRadius:12,
              padding:24,marginBottom:24,textAlign:'center'}}>
              <div style={{fontSize:28,marginBottom:8}}>📋</div>
              <div style={{fontSize:15,fontWeight:600,color:'#3B2A21',marginBottom:4}}>
                Start Date Not Yet Recorded
              </div>
              <div style={{fontSize:13,color:'#8A8A8A'}}>
                Your employment start date has not yet been entered in the system.
                Please speak to management to have this updated.
                Your loyalty wallet will be calculated from your start date.
              </div>
            </div>
          )}

          {!myData?.eligible && !myData?.no_start_date && myData?.months_until_eligible >= 0 && (
            <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:12,
              padding:24,marginBottom:24,textAlign:'center'}}>
              <div style={{fontSize:28,marginBottom:8}}>⏳</div>
              <div style={{fontSize:15,fontWeight:600,color:'#8B5E0A',marginBottom:4}}>
                Not Yet Eligible
              </div>
              <div style={{fontSize:13,color:'#8B5E0A',lineHeight:1.6}}>
                {myData.months_until_eligible > 0 ? (
                  <>Your loyalty wallet begins in <strong>{myData.months_until_eligible}</strong> month{myData.months_until_eligible!==1?'s':''}. </>
                ) : (
                  <>Your loyalty wallet is being set up. </>
                )}
                Bloans begins setting aside <strong>10% of your salary</strong> from your <strong>13th month</strong> of service.
                You have worked <strong>{myData.months_worked || 0}</strong> month{(myData.months_worked||0)!==1?'s':''} so far.
                The gift accumulates every month and will be paid to you in full when you complete a proper exit.
              </div>
            </div>
          )}

          {myData?.eligible && (
            <>
              {/* Wallet card */}
              <div style={{background:'linear-gradient(135deg,#2C1F16,#3B2A21)',
                borderRadius:16,padding:32,marginBottom:20,color:'#F2EFE7',
                position:'relative',overflow:'hidden'}}>
                <div style={{position:'absolute',top:-20,right:-20,width:120,height:120,
                  background:'radial-gradient(circle,rgba(168,137,61,.2) 0%,transparent 70%)'}}/>
                <div style={{fontSize:10,color:'rgba(242,239,231,.4)',
                  textTransform:'uppercase',letterSpacing:'.15em',marginBottom:8}}>
                  Total Accumulated
                </div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'3rem',
                  fontWeight:600,color:'#C9A96E',marginBottom:4}}>
                  {fmt(myData.summary?.total_accrued||0)}
                </div>
                <div style={{fontSize:12,color:'rgba(242,239,231,.5)'}}>
                  {myData.summary?.months_contributing||0} month{(myData.summary?.months_contributing||0)!==1?'s':''} of contributions
                </div>
                <div style={{display:'flex',gap:24,marginTop:24}}>
                  <div>
                    <div style={{fontSize:9,color:'rgba(242,239,231,.35)',
                      textTransform:'uppercase',letterSpacing:'.1em',marginBottom:4}}>
                      Monthly Gift
                    </div>
                    <div style={{fontSize:16,fontWeight:600,color:'#C9A96E'}}>
                      {fmt(myData.summary?.monthly_contribution || (myData.summary?.basic_salary||myData.staff?.base_salary||0)*0.1)}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:'rgba(242,239,231,.35)',
                      textTransform:'uppercase',letterSpacing:'.1em',marginBottom:4}}>
                      Based On
                    </div>
                    <div style={{fontSize:16,fontWeight:600,color:'#C9A96E'}}>
                      {fmt(myData.summary?.basic_salary||myData.staff?.base_salary||0)} salary
                    </div>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div style={{background:'#F0F7F1',border:'1px solid #B8DEC0',borderRadius:10,
                padding:16,marginBottom:24,fontSize:12,color:'#2E6B3A',lineHeight:1.6}}>
                💡 This amount is a gift from Bloans — it is never deducted from your salary. It will be paid to you in full when you complete a proper exit from the company.
              </div>

              {/* No contributions yet message */}
              {myData.eligible && (!myData.contributions || myData.contributions.length === 0) && (
                <div style={{background:'#F0F7F1',border:'1px solid #B8DEC0',borderRadius:10,
                  padding:16,marginBottom:20,fontSize:13,color:'#2E6B3A',lineHeight:1.6,textAlign:'center'}}>
                  ✅ You are eligible for the loyalty wallet. Contributions will appear here once your payroll is processed.
                </div>
              )}

              {/* Monthly history */}
              <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,
                overflow:'hidden'}}>
                <div style={{padding:'16px 20px',borderBottom:'1px solid #E8E0D4',
                  display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>Monthly History</div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>{myData.contributions.length} entries</div>
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#3B2A21'}}>
                      {['Month','Salary at Time','Gift Amount','Status'].map(h=>(
                        <th key={h} style={{padding:'10px 16px',textAlign:'left',
                          fontSize:9,color:'#E8C466',textTransform:'uppercase',
                          letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(myData.contributions||[]).map((c,i)=>(
                      <tr key={c.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'10px 16px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>
                          {MONTHS[c.month-1]} {c.year}
                        </td>
                        <td style={{padding:'10px 16px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                          {fmt(c.salary_at_time)}
                        </td>
                        <td style={{padding:'10px 16px',borderBottom:'1px solid #EDE7D8',
                          fontWeight:600,color:'#A8893D'}}>
                          {fmt(c.contribution_amount)}
                        </td>
                        <td style={{padding:'10px 16px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:10,padding:'3px 10px',borderRadius:20,fontWeight:600,
                            background:c.status==='accruing'?'#E8F5EB':c.status==='paid_out'?'#EDE7D8':'#FDEAEA',
                            color:c.status==='accruing'?'#2E6B3A':c.status==='paid_out'?'#8B5E0A':'#8F2E24'}}>
                            {c.status==='accruing'?'Accruing':c.status==='paid_out'?'Paid Out':'Forfeited'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── ADMIN VIEW ───────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',
            color:'#3B2A21',margin:'0 0 4px'}}>Steward Loyalty Contributions</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>
            10% of salary set aside monthly per eligible Steward. Not deducted from pay.
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-outline"
            onClick={()=>setShowManualModal(true)}>
            + Manual Entry
          </button>
        </div>
      </div>

      {/* Total liability card */}
      <div style={{background:'linear-gradient(135deg,#2C1F16,#3B2A21)',
        borderRadius:12,padding:24,marginBottom:24,
        display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontSize:10,color:'rgba(242,239,231,.4)',
            textTransform:'uppercase',letterSpacing:'.12em',marginBottom:6}}>
            Total Company Liability
          </div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2.5rem',
            fontWeight:600,color:'#C9A96E'}}>
            {fmt(totalLiability)}
          </div>
          <div style={{fontSize:11,color:'rgba(242,239,231,.4)',marginTop:4}}>
            Accrued across all active Stewards
          </div>
        </div>
        <div style={{fontSize:40,opacity:.3}}>🏦</div>
      </div>

      {/* Staff summary table */}
      {loading ? <div className="spinner"/> : (
        <div style={{display:'grid',gridTemplateColumns:selectedStaff?'1fr 380px':'1fr',gap:16}}>

          {/* Left: staff list */}
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
            <div style={{padding:'14px 20px',borderBottom:'1px solid #E8E0D4',
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>
                Eligible Stewards ({summary.length})
              </div>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#3B2A21'}}>
                  {['Steward','Months','Monthly Gift','Total Accrued','Status',''].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((s,i)=>(
                  <tr key={s.staff_id}
                    style={{background:selectedStaff?.staff_id===s.staff_id?'rgba(168,137,61,.08)':i%2===0?'#fff':'#F9F8F5',
                      cursor:'pointer'}}
                    onClick={()=>loadStaffDetail(s)}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600}}>
                      {s.name}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                      {s.months_contributing}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#A8893D',fontWeight:600}}>
                      {fmt(parseFloat(s.basic_salary||0)*0.1)}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                      fontWeight:700,color:'#2C1F16'}}>
                      {fmt(s.total_accrued)}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,padding:'2px 8px',borderRadius:20,fontWeight:600,
                        background:s.total_paid>0?'#EDE7D8':s.total_forfeited>0?'#FDEAEA':'#E8F5EB',
                        color:s.total_paid>0?'#8B5E0A':s.total_forfeited>0?'#8F2E24':'#2E6B3A'}}>
                        {s.total_paid>0?'Paid Out':s.total_forfeited>0?'Forfeited':'Accruing'}
                      </span>
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{color:'#A8893D',fontSize:12}}>→</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right: staff detail */}
          {selectedStaff && staffDetail && (
            <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,
              overflow:'hidden',height:'fit-content'}}>
              <div style={{background:'#2C1F16',padding:'16px 20px',
                display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:'#F2EFE7'}}>{selectedStaff.name}</div>
                  <div style={{fontSize:10,color:'rgba(242,239,231,.4)',marginTop:2}}>
                    Joined: {staffDetail.summary?.date_joined ? new Date(staffDetail.summary.date_joined).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                  </div>
                </div>
                <button onClick={()=>setSelectedStaff(null)}
                  style={{background:'none',border:'none',color:'rgba(242,239,231,.5)',
                    cursor:'pointer',fontSize:16}}>×</button>
              </div>

              {/* Summary */}
              <div style={{padding:16,borderBottom:'1px solid #E8E0D4'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                  {[
                    ['Total Accrued', fmt(staffDetail.summary?.total_accrued||0), '#A8893D'],
                    ['Months', staffDetail.summary?.months_contributing||0, '#556B2F'],
                  ].map(([label,val,color])=>(
                    <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:12}}>
                      <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',
                        letterSpacing:'.08em',marginBottom:4}}>{label}</div>
                      <div style={{fontSize:18,fontWeight:700,color}}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Payout info */}
                {staffDetail.payout ? (
                  <div style={{background:'#F0F7F1',border:'1px solid #B8DEC0',
                    borderRadius:8,padding:12,fontSize:12}}>
                    <div style={{fontWeight:600,color:'#2E6B3A',marginBottom:4}}>✓ Paid Out</div>
                    <div style={{color:'#555'}}>{fmt(staffDetail.payout.amount_paid)} on {new Date(staffDetail.payout.payment_date).toLocaleDateString('en-NG')}</div>
                    <div style={{color:'#8A8A8A',marginTop:2}}>via {staffDetail.payout.payment_method}</div>
                  </div>
                ) : (
                  parseFloat(staffDetail.summary?.total_accrued||0) > 0 && (
                    <button className="btn btn-primary" style={{width:'100%'}}
                      onClick={()=>{setPayoutForm({payment_date:'',payment_method:'',exit_type:'proper',reference:'',notes:''});setShowPayoutModal(true);}}>
                      Process Payout
                    </button>
                  )
                )}
              </div>

              {/* Monthly history */}
              <div style={{maxHeight:300,overflowY:'auto'}}>
                {staffDetail.contributions?.map((c,i)=>(
                  <div key={c.id} style={{padding:'10px 16px',borderBottom:'1px solid #F2EFE7',
                    display:'flex',justifyContent:'space-between',alignItems:'center',
                    background:i%2===0?'#fff':'#F9F8F5'}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>
                        {MONTHS[c.month-1]} {c.year}
                      </div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>
                        Salary: {fmt(c.salary_at_time)}
                      </div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:13,fontWeight:700,color:'#A8893D'}}>
                        {fmt(c.contribution_amount)}
                      </div>
                      <span style={{fontSize:9,padding:'1px 6px',borderRadius:10,
                        background:c.status==='accruing'?'#E8F5EB':'#EDE7D8',
                        color:c.status==='accruing'?'#2E6B3A':'#8B5E0A'}}>
                        {c.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr">
              <span className="modal-title">Process Payout — {selectedStaff?.name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPayoutModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16,
                fontSize:13,color:'#3B2A21',textAlign:'center'}}>
                Total to pay: <strong style={{color:'#A8893D',fontSize:16}}>
                  {fmt(staffDetail?.summary?.total_accrued||0)}
                </strong>
              </div>
              <div className="form-group">
                <label className="form-label">Exit Type *</label>
                <select className="form-control" value={payoutForm.exit_type}
                  onChange={e=>setPayoutForm({...payoutForm,exit_type:e.target.value})}>
                  <option value="proper">Proper Exit — Pay in Full</option>
                  <option value="improper">Improper Exit — Forfeit</option>
                </select>
              </div>
              {payoutForm.exit_type==='proper' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Payment Date *</label>
                    <input type="date" className="form-control" value={payoutForm.payment_date}
                      onChange={e=>setPayoutForm({...payoutForm,payment_date:e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method *</label>
                    <select className="form-control" value={payoutForm.payment_method}
                      onChange={e=>setPayoutForm({...payoutForm,payment_method:e.target.value})}>
                      <option value="">Select...</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reference</label>
                    <input className="form-control" value={payoutForm.reference}
                      onChange={e=>setPayoutForm({...payoutForm,reference:e.target.value})}
                      placeholder="Transfer reference or receipt number" />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={payoutForm.notes}
                  onChange={e=>setPayoutForm({...payoutForm,notes:e.target.value})} />
              </div>
              {payoutForm.exit_type==='improper' && (
                <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
                  padding:12,fontSize:12,color:'#8F2E24'}}>
                  ⚠ Marking as improper exit will forfeit all accumulated contributions. This cannot be undone.
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPayoutModal(false)}>Cancel</button>
              <button className="btn btn-primary"
                style={{background:payoutForm.exit_type==='improper'?'#8F2E24':'#556B2F',border:'none'}}
                onClick={processPayout} disabled={saving}>
                {saving?'Processing...:':payoutForm.exit_type==='proper'?'Confirm Payout':'Confirm Forfeiture'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-hdr">
              <span className="modal-title">Manual Contribution Entry</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowManualModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Staff ID</label>
                <input className="form-control" value={manualForm.staff_id}
                  onChange={e=>setManualForm({...manualForm,staff_id:e.target.value})}
                  placeholder="Staff UUID" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Month</label>
                  <select className="form-control" value={manualForm.month}
                    onChange={e=>setManualForm({...manualForm,month:e.target.value})}>
                    <option value="">Month</option>
                    {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Year</label>
                  <input type="number" className="form-control" value={manualForm.year}
                    onChange={e=>setManualForm({...manualForm,year:e.target.value})}
                    placeholder="2025" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Salary at Time</label>
                <input type="number" className="form-control" value={manualForm.salary_at_time}
                  onChange={e=>setManualForm({...manualForm,salary_at_time:e.target.value})}
                  placeholder="Gross salary amount" />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={manualForm.notes}
                  onChange={e=>setManualForm({...manualForm,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowManualModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={processManual} disabled={saving}>
                {saving?'Saving...':'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
